/**
 * 認証済みルートガード
 *
 * 【このコンポーネントの役割】
 * 認証が必要なページを「保護」する門番。
 * ログインしていないユーザーがアクセスしようとしたら、
 * ログイン画面にリダイレクトする。
 *
 * 【判定フロー】
 * 1. isLoading=true → 読み込み中（/auth/me の応答待ち）→ スピナー表示
 * 2. isAuthenticated=false → 未認証 → /login へリダイレクト
 * 3. must_change_password=true → パスワード変更が必要 → /auth/change-password へ
 * 4. canView=false → 権限不足 → /home へリダイレクト
 * 5. すべてOK → 子コンポーネントを表示
 *
 * 【改修前との違い】
 * - user.permissionLevel === undefined の旧セッション判定を削除
 *   （localStorage ベースの復元がなくなったため不要）
 * - must_change_password チェックを追加
 * - useAuth() から logout を使わなくなった
 *   （権限不足の場合はログアウトではなくリダイレクトに変更）
 */

import { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { LoadingIndicator } from './atoms/LoadingIndicator';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { canView } = usePermission();
  const location = useLocation();

  // /auth/me の応答待ち
  if (isLoading) {
    return (
      <div className="min-h-svh flex items-center justify-center bg-[#E9F1F2]">
        <LoadingIndicator size="lg" />
      </div>
    );
  }

  // 未認証 → ログイン画面へ
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // 個人情報同意が未完了（同意画面自体は除外）
  if (user?.privacyConsentAt === null && location.pathname !== '/auth/privacy-consent') {
    return <Navigate to="/auth/privacy-consent" replace />;
  }

  // 初回パスワード変更が必要（ただしパスワード変更画面・同意画面は除外）
  if (user?.mustChangePassword && location.pathname !== '/auth/change-password' && location.pathname !== '/auth/privacy-consent') {
    return <Navigate to="/auth/change-password" replace />;
  }

  // 権限がないページへのアクセスはホームへリダイレクト
  if (!canView) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
